/**
 * Org web layout — a hierarchical "family tree" by rank. Extracted from
 * src/pages/Staff.jsx so the geometry is unit-testable (src/lib/orgTree.test.js);
 * the page component only renders what this returns.
 *
 * Defera sits at the top. Rank maps to row: managers (and executives, when
 * the full-hierarchy view adds the human leadership tier) on the first rows,
 * their reports beneath them, unmanaged specialists on the specialist tier
 * tethered straight to the root. Departments cluster horizontally inside
 * faint lanes. Everything is computed from the roster — new members, deeper
 * reporting chains, and new departments just extend the tree; no hand-placed
 * coordinates anywhere.
 *
 * Full-hierarchy support: a section flagged `noLane: true` (the human
 * leadership tier) gets NO department lane/label, and its members' x
 * positions are computed from their ROSTER-WIDE children (an executive
 * centers over every department they oversee, across lanes) instead of the
 * per-department pass. Department lanes then start below the deepest noLane
 * tier so executives float above the lanes rather than inside one.
 */

export function computeTreeLayout({ sections, roster, baseW, margin, minSpacing, rootY, tierGap, nodeR, labelFont = 10, bottomPad = 0 }) {
  const byId = new Map(roster.map((m) => [m.id, m]));

  // Row per member: beneath their manager when one exists on the roster,
  // otherwise rank decides (managers/executives tier 1, everyone else tier 2).
  const tierCache = new Map();
  const tierOf = (member, trail = new Set()) => {
    if (tierCache.has(member.id)) return tierCache.get(member.id);
    let tier;
    const manager = member.reportsTo ? byId.get(member.reportsTo) : null;
    if (manager && !trail.has(member.id)) {
      tier = tierOf(manager, new Set(trail).add(member.id)) + 1;
    } else {
      tier = member.rank === 'manager' || member.rank === 'executive' ? 1 : 2;
    }
    tierCache.set(member.id, tier);
    return tier;
  };
  roster.forEach((m) => tierOf(m));

  // Leaf slots left→right, departments contiguous, reports directly under
  // their manager (managers take the midpoint of their subtree).
  const slotCount = roster.filter((m) => !roster.some((x) => x.reportsTo === m.id)).length;
  const W = Math.max(baseW, margin * 2 + minSpacing * Math.max(slotCount - 1, 0));
  const spacing = slotCount > 1 ? (W - margin * 2) / (slotCount - 1) : 0;
  let nextSlot = 0;
  const slotX = () => (slotCount > 1 ? margin + (nextSlot++) * spacing : W / 2);

  const xById = {};
  const deptRanges = [];
  for (const dept of sections) {
    if (dept.noLane) continue; // leadership tier — positioned globally below
    const inDept = new Set(dept.members.map((m) => m.id));
    const childrenOf = (m) => dept.members.filter((x) => x.reportsTo === m.id);
    const assignX = (m) => {
      if (xById[m.id] != null) return xById[m.id];
      const kids = childrenOf(m);
      const x = kids.length
        ? kids.map(assignX).reduce((a, b) => a + b, 0) / kids.length
        : slotX();
      xById[m.id] = x;
      return x;
    };
    // Department roots: members whose manager isn't in this department.
    dept.members
      .filter((m) => !m.reportsTo || !inDept.has(m.reportsTo))
      .forEach(assignX);
    const xs = dept.members.map((m) => xById[m.id]);
    deptRanges.push({
      id: dept.id,
      name: dept.name,
      count: dept.members.length,
      xMin: Math.min(...xs),
      xMax: Math.max(...xs),
    });
  }

  // noLane members (executives): midpoint of their roster-wide children,
  // computed bottom-up so the CEO centers over the executive row, which in
  // turn centers over the department heads. An executive with no assigned
  // reports falls back to a leaf slot.
  const noLaneMembers = sections.filter((s) => s.noLane).flatMap((s) => s.members);
  const assignXGlobal = (m, trail = new Set()) => {
    if (xById[m.id] != null) return xById[m.id];
    const kids = roster.filter((x) => x.reportsTo === m.id && !trail.has(x.id));
    const x = kids.length
      ? kids.map((k) => assignXGlobal(k, new Set(trail).add(m.id))).reduce((a, b) => a + b, 0) / kids.length
      : slotX();
    xById[m.id] = x;
    return x;
  };
  noLaneMembers.forEach((m) => assignXGlobal(m));

  // Wrap department labels to their lane width up-front so the canvas
  // can reserve vertical room for the tallest label stack.
  const lanePad = Math.max(44, Math.min(66, spacing * 0.42 || 60));
  const estTextWidth = (s) => s.length * (labelFont * 0.68 + 1.4);
  const wrapLabel = (text, maxW) => {
    const lines = [];
    let current = '';
    for (const word of text.split(' ')) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && estTextWidth(candidate) > maxW) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
    return lines;
  };
  const linesByDept = deptRanges.map((d) => {
    const laneW = (d.xMax - d.xMin) + lanePad * 2;
    const lines = wrapLabel(d.name.toUpperCase(), laneW - 8);
    // Count rides on the last line — never a lonely "· 1" row.
    lines[lines.length - 1] += ` · ${d.count}`;
    return lines;
  });
  const maxLabelLines = Math.max(1, ...linesByDept.map((l) => l.length));

  const maxTier = Math.max(2, ...roster.map((m) => tierCache.get(m.id)));
  // bottomPad: extra headroom under the last tier — used when per-node
  // title lines render below names so they never brush the lane labels.
  const H = rootY + maxTier * tierGap + nodeR + 16 + 44 + bottomPad + (maxLabelLines - 1) * (labelFont + 3);

  const nodes = [];
  for (const m of roster) {
    nodes.push({ member: m, x: xById[m.id] ?? W / 2, y: rootY + tierCache.get(m.id) * tierGap, tier: tierCache.get(m.id) });
  }
  const posById = Object.fromEntries(nodes.map((n) => [n.member.id, n]));

  const rootX = W / 2;
  const edges = nodes.map((n) => {
    const m = n.member;
    const parent = m.reportsTo ? posById[m.reportsTo] : null;
    if (parent) {
      return { id: `${m.reportsTo}-${m.id}`, x1: parent.x, y1: parent.y + nodeR, x2: n.x, y2: n.y - nodeR, kind: 'reports' };
    }
    return { id: `core-${m.id}`, x1: rootX, y1: rootY, x2: n.x, y2: n.y - nodeR, kind: 'core' };
  });

  // Department lanes + bottom labels. Labels wrap within their own lane,
  // so adjacent departments can never collide no matter how narrow a
  // one-member lane gets. When a leadership (noLane) tier exists, lanes
  // start below its deepest row so executives sit above the lanes.
  const laneTierBase = noLaneMembers.length
    ? Math.max(...noLaneMembers.map((m) => tierCache.get(m.id)))
    : 0;
  const laneTop = rootY + Math.round(tierGap * (laneTierBase + 0.52));
  const deptLabels = deptRanges.map((d, i) => ({
    ...d,
    laneX: d.xMin - lanePad,
    laneW: (d.xMax - d.xMin) + lanePad * 2,
    laneY: laneTop,
    laneH: H - laneTop - 10,
    x: (d.xMin + d.xMax) / 2,
    y: H - 16,
    lines: linesByDept[i],
  }));

  return { nodes, edges, deptLabels, W, H, rootX, rootY };
}

/** Vertical S-curve between rows — family-tree strands, not wire diagrams. */
export function edgePath(e) {
  const midY = (e.y1 + e.y2) / 2;
  return `M ${e.x1} ${e.y1} C ${e.x1} ${midY}, ${e.x2} ${midY}, ${e.x2} ${e.y2}`;
}

/**
 * Story layout — the full hierarchy recomposed for Instagram-Story
 * dimensions (1080×1920 portrait). Rank semantics are preserved exactly:
 * logo → CEO → executive row → each executive's departments stacked in
 * THEIR OWN COLUMN beneath them (department manager above specialists
 * inside each card). Nothing about who ranks where changes — only the
 * reading direction (top-down columns instead of left-right lanes).
 *
 * @param leadership [{id,name,title,avatar,reportsTo,rank:'executive'}] —
 *                   exactly one CEO (reportsTo null), the rest execs.
 * @param sections   AI department sections [{id,name,members}] in display
 *                   order (columns preserve this order top-to-bottom).
 * @param oversight  deptId → execId mapping.
 */
export function computeStoryLayout({ leadership, sections, oversight, W = 1080, H = 1920 }) {
  const ceo = leadership.find((l) => !l.reportsTo) || leadership[0];
  const execs = leadership.filter((l) => l !== ceo);
  const colW = W / Math.max(execs.length, 1);

  const nodeR = 40;
  const execR = 56;
  const ceoR = 66;
  const logo = { x: W / 2, y: 120, r: 64 };
  const ceoNode = { member: ceo, x: W / 2, y: 320, r: ceoR };
  const execY = 570;
  const execNodes = execs.map((e, i) => ({ member: e, x: colW * i + colW / 2, y: execY, r: execR }));

  const cardTop = execY + 120;
  const cardPad = 16;
  const labelH = 34;
  // Face + name + two wrapped title lines + a bottom gutter, so a
  // manager's second title line can never brush the next row's faces.
  const rowH = nodeR * 2 + 65;
  const gapY = 21;

  const nodes = [];
  const cards = [];
  const colBottoms = execs.map(() => cardTop);

  for (const sec of sections) {
    const col = execs.findIndex((e) => e.id === oversight[sec.id]);
    if (col === -1 || !sec.members.length) continue;
    // Manager row above everyone else's row — rank stays visually intact.
    const managers = sec.members.filter((m) => m.rank === 'manager');
    const others = sec.members.filter((m) => m.rank !== 'manager');
    const rows = [managers, others].filter((r) => r.length);
    const cardW = colW - 28;
    const x = colW * col + 14;
    const h = cardPad * 2 + labelH + rows.length * rowH;
    const y = colBottoms[col];
    cards.push({ id: sec.id, name: sec.name, x, y, w: cardW, h, count: sec.members.length, execId: oversight[sec.id] });
    rows.forEach((row, ri) => {
      const rowY = y + cardPad + labelH + ri * rowH + nodeR + 4;
      const step = cardW / (row.length + 1);
      row.forEach((m, mi) => {
        nodes.push({ member: m, x: x + step * (mi + 1), y: rowY, r: nodeR, row: ri });
      });
    });
    colBottoms[col] = y + h + gapY;
  }

  // Strands: logo→CEO, CEO→execs, and one spine per exec down their column.
  const edges = [
    { x1: logo.x, y1: logo.y + logo.r, x2: ceoNode.x, y2: ceoNode.y - ceoR, kind: 'core' },
    ...execNodes.map((e) => ({ x1: ceoNode.x, y1: ceoNode.y + ceoR, x2: e.x, y2: e.y - execR, kind: 'reports' })),
    ...execNodes.map((e, i) => {
      const last = colBottoms[i] - gapY;
      return last > cardTop
        ? { x1: e.x, y1: e.y + execR, x2: e.x, y2: last, kind: 'spine' }
        : null;
    }).filter(Boolean),
  ];

  // Height stays exactly story-shaped as long as content fits; if a future
  // roster overflows, the canvas grows (aspect bends rather than clipping
  // people out of the org). colBottoms carry a trailing gap — swap it for
  // the real bottom margin.
  const height = Math.max(H, Math.max(...colBottoms, cardTop) - gapY + 24);
  return { W, H: height, logo, ceoNode, execNodes, cards, nodes, edges, colWidth: colW };
}
